import React from "react";
import { motion } from "motion/react";
import { HiSparkles } from "react-icons/hi2";
import {
    FiZap,
    FiHeart,
    FiTarget,
    FiUsers,
    FiTrendingUp,
    FiCheck,
    FiArrowRight,
    FiAward,
    FiLayers,
    FiSmile,
} from "react-icons/fi";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientAdSense from "../components/Client/ClientAdSense";

const FEATURES = [
    {
        icon: FiZap,
        title: "AI-Powered Prompts",
        desc: "Curated, intelligent prompts that spark creativity in seconds.",
        gradient: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
    },
    {
        icon: FiLayers,
        title: "Endless Variety",
        desc: "A constantly expanding library covering every style and theme.",
        gradient: "linear-gradient(135deg, #FCFA02, #FF8AFF)",
    },
    {
        icon: FiSmile,
        title: "Beginner Friendly",
        desc: "Simple, intuitive UI designed for both novices and pros.",
        gradient: "linear-gradient(135deg, #FF8AFF, #7F00FF)",
    },
    {
        icon: FiTrendingUp,
        title: "Always Growing",
        desc: "New prompts and categories added regularly — never run dry.",
        gradient: "linear-gradient(135deg, #7F00FF, #FCFA02)",
    },
];

const STATS = [
    { value: "100K+", label: "Downloads", icon: FiUsers },
    { value: "10K+", label: "Prompts", icon: FiZap },
    { value: "50+", label: "Categories", icon: FiLayers },
    { value: "4.8★", label: "Rating", icon: FiAward },
];

const VALUES = [
    {
        title: "Creativity First",
        desc: "We believe everyone has a story worth telling — we just help you tell it visually.",
    },
    {
        title: "Built for Everyone",
        desc: "From first-time creators to seasoned artists, our tools meet you where you are.",
    },
    {
        title: "Free Forever",
        desc: "Core features stay free — we want creativity to be accessible, not gatekept.",
    },
];

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const AboutUs = () => {
    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />

            {/* ===== HERO ===== */}
            <section className='relative w-full overflow-hidden'>
                {/* Animated gradient orbs */}
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 50, -30, 0], y: [0, -40, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -left-20 w-80 h-80 rounded-full opacity-40 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-32 -right-20 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-6xl px-4 sm:px-6 md:px-8 pt-10 md:pt-16 pb-12 md:pb-20'>
                    {/* DSPY1 — top-of-page display ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10 text-left'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className='text-center'
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-5'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>About PicPromptify</span>
                        </div>

                        <h1 className='text-4xl md:text-6xl lg:text-7xl font-bold text-client-font leading-[1.05] tracking-tight'>
                            Crafting{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                creativity
                            </span>
                            <br />
                            for everyone
                        </h1>

                        <p className='text-client-font/65 text-sm md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed'>
                            PicPromptify is an AI-powered prompt platform built to spark
                            inspiration — turning blank canvases into bold ideas, one prompt
                            at a time.
                        </p>
                    </motion.div>

                    {/* Stats strip */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className='mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4'
                    >
                        {STATS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={s.label}
                                    whileHover={{ y: -4, scale: 1.03 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                    className='ios-glass-dark rounded-2xl p-4 md:p-5 flex items-center gap-3 md:gap-4'
                                    style={{
                                        boxShadow:
                                            "0 10px 28px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)",
                                    }}
                                >
                                    <div
                                        className='w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, rgba(127,0,255,0.3), rgba(252,250,2,0.15))",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                        }}
                                    >
                                        <Icon
                                            className='text-base md:text-xl'
                                            style={{ color: "#FCFA02" }}
                                        />
                                    </div>
                                    <div className='min-w-0'>
                                        <div className='text-client-font font-bold text-lg md:text-2xl tracking-tight'>
                                            {s.value}
                                        </div>
                                        <div className='text-client-font/55 text-[10px] md:text-xs uppercase tracking-wider'>
                                            {s.label}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* ===== STORY ===== */}
            <section className='w-full px-4 sm:px-6 md:px-8 py-12 md:py-20'>
                <div className='mx-auto max-w-5xl'>
                    <motion.div {...fadeUp} className='ios-glass-dark rounded-3xl p-6 md:p-12 relative overflow-hidden'>
                        <div
                            aria-hidden
                            className='absolute -top-20 -right-16 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                            }}
                        />
                        <div className='relative flex items-start gap-3 mb-5'>
                            <div
                                className='w-1 h-8 rounded-full flex-shrink-0'
                                style={{
                                    background:
                                        "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                }}
                            />
                            <div>
                                <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-1'>
                                    Our Story
                                </p>
                                <h2 className='text-2xl md:text-4xl font-bold text-client-font tracking-tight'>
                                    Born from a love of creating
                                </h2>
                            </div>
                        </div>
                        <p className='relative text-client-font/75 text-sm md:text-lg leading-relaxed max-w-3xl'>
                            PicPromptify is an innovative platform designed to enhance photo
                            editing and creative experiences by providing a wide array of
                            AI-generated prompts. We're categorized under Utilities & Tools,
                            offering users a free license — making creative inspiration
                            accessible to anyone, anywhere. With our intuitive interface and
                            a constantly expanding collection of prompts, we help creators
                            of every level explore, experiment, and elevate.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* ===== FEATURES GRID ===== */}
            <section className='w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-6xl'>
                    <motion.div {...fadeUp} className='text-center mb-8 md:mb-12'>
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1 rounded-full text-[10px] md:text-xs text-client-font/85 mb-3'>
                            <FiZap style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>What We Offer</span>
                        </div>
                        <h2 className='text-3xl md:text-5xl font-bold text-client-font tracking-tight'>
                            Built for{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #7F00FF, #FF8AFF, #FCFA02)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                creators
                            </span>
                        </h2>
                    </motion.div>

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5'>
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <motion.div
                                    key={f.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{
                                        duration: 0.55,
                                        delay: i * 0.08,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    whileHover={{ y: -6 }}
                                    className='group relative ios-glass-dark rounded-2xl p-5 md:p-7 overflow-hidden'
                                    style={{
                                        boxShadow:
                                            "0 12px 32px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.12)",
                                    }}
                                >
                                    <div
                                        aria-hidden
                                        className='absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-3xl pointer-events-none'
                                        style={{ background: f.gradient }}
                                    />
                                    <div className='relative flex items-start gap-4'>
                                        <motion.div
                                            whileHover={{ rotate: 8, scale: 1.05 }}
                                            transition={{ type: "spring", stiffness: 300, damping: 14 }}
                                            className='w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center flex-shrink-0'
                                            style={{
                                                background: f.gradient,
                                                boxShadow:
                                                    "0 8px 22px -6px rgba(127,0,255,0.45)",
                                            }}
                                        >
                                            <Icon className='text-xl md:text-2xl text-white' />
                                        </motion.div>
                                        <div className='min-w-0'>
                                            <h3 className='text-client-font font-bold text-lg md:text-xl mb-1.5'>
                                                {f.title}
                                            </h3>
                                            <p className='text-client-font/60 text-sm md:text-base leading-relaxed'>
                                                {f.desc}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== MISSION ===== */}
            <section className='w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-5xl'>
                    <motion.div
                        {...fadeUp}
                        className='relative rounded-3xl p-6 md:p-12 overflow-hidden'
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(127,0,255,0.18), rgba(252,250,2,0.06))",
                            border: "1px solid rgba(255,255,255,0.12)",
                            backdropFilter: "blur(18px) saturate(180%)",
                            WebkitBackdropFilter: "blur(18px) saturate(180%)",
                        }}
                    >
                        <motion.div
                            aria-hidden
                            animate={{ rotate: 360 }}
                            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                            className='absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                            }}
                        />

                        <div className='relative grid md:grid-cols-3 gap-6 md:gap-10 items-start'>
                            <div className='md:col-span-1'>
                                <div
                                    className='inline-flex w-16 h-16 md:w-20 md:h-20 rounded-2xl items-center justify-center mb-4'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        boxShadow:
                                            "0 12px 30px -8px rgba(127,0,255,0.6)",
                                    }}
                                >
                                    <FiTarget className='text-2xl md:text-3xl text-white' />
                                </div>
                                <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-1'>
                                    Our Mission
                                </p>
                                <h2 className='text-2xl md:text-3xl font-bold text-client-font tracking-tight leading-tight'>
                                    Democratize creativity
                                </h2>
                            </div>
                            <div className='md:col-span-2'>
                                <p className='text-client-font/80 text-base md:text-lg leading-relaxed mb-6'>
                                    We believe everyone has the potential to create stunning
                                    visual content. Our mission is to make AI-powered creative
                                    inspiration accessible to all — helping users unlock their
                                    potential and turn fleeting ideas into reality.
                                </p>
                                <div className='space-y-3'>
                                    {VALUES.map((v, i) => (
                                        <motion.div
                                            key={v.title}
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4, delay: i * 0.1 }}
                                            className='flex items-start gap-3'
                                        >
                                            <div
                                                className='mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0'
                                                style={{
                                                    background:
                                                        "linear-gradient(135deg, #FCFA02, #FF8AFF)",
                                                }}
                                            >
                                                <FiCheck className='text-[10px] text-client-back font-bold' />
                                            </div>
                                            <div>
                                                <h4 className='text-client-font font-semibold text-sm md:text-base'>
                                                    {v.title}
                                                </h4>
                                                <p className='text-client-font/55 text-xs md:text-sm leading-relaxed'>
                                                    {v.desc}
                                                </p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ===== CTA ===== */}
            <section className='w-full px-4 sm:px-6 md:px-8 pb-16 md:pb-24'>
                <div className='mx-auto max-w-4xl'>
                    <motion.div
                        {...fadeUp}
                        className='ios-glass-dark relative rounded-3xl p-6 md:p-10 text-center overflow-hidden'
                    >
                        <div
                            aria-hidden
                            className='absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                            }}
                        />
                        <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className='relative inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl mb-4'
                            style={{
                                background:
                                    "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                boxShadow: "0 12px 30px -8px rgba(127,0,255,0.55)",
                            }}
                        >
                            <FiHeart className='text-2xl md:text-3xl text-white' />
                        </motion.div>
                        <h3 className='relative text-2xl md:text-4xl font-bold text-client-font tracking-tight mb-2'>
                            Ready to create something amazing?
                        </h3>
                        <p className='relative text-client-font/60 text-sm md:text-base mb-6 max-w-xl mx-auto'>
                            Join thousands of creators already using PicPromptify to bring
                            their wildest ideas to life.
                        </p>
                        <motion.a
                            href='/'
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            className='relative inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5 rounded-full text-white font-semibold text-sm md:text-base'
                            style={{
                                background:
                                    "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                boxShadow:
                                    "0 14px 32px -8px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                            }}
                        >
                            Explore Prompts
                            <FiArrowRight />
                        </motion.a>
                    </motion.div>
                </div>
            </section>

            <FixedButton />
            <ClientFooter />
        </div>
    );
};

export default AboutUs;
